import { Organization, Store, Device, Venue } from '../types';

// Define selection types
export type SelectionEventHandler<T extends Device = Device> = (
  selection: SelectionState<T>,
) => void;
export type SelectionEventType = 'selection-cleared' | 'selection-changed';

export interface SelectionState<T extends Device = Device> {
  organization?: Pick<Organization, 'id' | 'name'>;
  store?: Pick<Store, 'id' | 'name'> & { venue?: Pick<Venue, 'id' | 'name'> };
  device?: T;
}

// Abstract storage interface
export interface SelectionStorage {
  store(key: string, value: string): Promise<void>;
  retrieve(key: string): Promise<string | null>;
  remove(key: string): Promise<void>;
}

// Storage keys
export const STORAGE_KEYS = {
  ORGANIZATION: 'selected_organization',
  STORE: 'selected_store',
  DEVICE: 'selected_device',
} as const;

/**
 * Selection service for managing onboarding flow state
 * Handles organization, store, and device selection with persistence
 *
 * This is a singleton service that ensures only one instance exists at any time,
 * providing consistent state management across the application.
 */
export class SelectionService<T extends Device = Device> {
  private static instance: SelectionService | null = null;
  private static isInitializing: boolean = false;
  private selectionState: SelectionState<T> = {};
  private eventHandlers: Map<
    SelectionEventType,
    Set<SelectionEventHandler<T>>
  > = new Map();
  private storage: SelectionStorage;

  private constructor(storage: SelectionStorage) {
    this.storage = storage;
  }

  static getInstance<T extends Device = Device>(
    storage?: SelectionStorage,
  ): SelectionService<T> {
    // Prevent concurrent initialization
    if (SelectionService.isInitializing) {
      throw new Error('SelectionService is already being initialized');
    }

    if (!SelectionService.instance) {
      if (!storage) {
        throw new Error(
          'SelectionService requires a storage implementation on first initialization',
        );
      }

      SelectionService.isInitializing = true;
      SelectionService.instance = new SelectionService(storage);
      SelectionService.isInitializing = false;
    }

    return SelectionService.instance as unknown as SelectionService<T>;
  }

  /**
   * Clear the singleton instance (useful for testing)
   * @internal
   */
  static clearInstance(): void {
    SelectionService.instance = null;
    SelectionService.isInitializing = false;
  }

  /**
   * Register an event handler
   * @param eventType The event type to listen for
   * @param handler The function to call when the event occurs
   */
  addEventListener(
    eventType: SelectionEventType,
    handler: SelectionEventHandler<T>,
  ): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set());
    }
    this.eventHandlers.get(eventType)?.add(handler);
  }

  /**
   * Remove an event handler
   * @param eventType The event type
   * @param handler The handler to remove
   */
  removeEventListener(
    eventType: SelectionEventType,
    handler: SelectionEventHandler<T>,
  ): void {
    this.eventHandlers.get(eventType)?.delete(handler);
  }

  /**
   * Emit an event to all registered handlers
   * @param eventType The event type to emit
   */
  private emitEvent(eventType: SelectionEventType): void {
    // Call all registered event handlers
    this.eventHandlers.get(eventType)?.forEach(handler => {
      handler(this.selectionState);
    });
  }

  /**
   * Get the current selection state
   * @returns Current selection state
   */
  getSelectionState(): SelectionState<T> {
    return { ...this.selectionState };
  }

  /**
   * Select an organization
   * @param organization The organization to select
   */
  async selectOrganization(organization: Organization): Promise<void> {
    this.selectionState.organization = organization;

    // Store the selected organization
    await this.storage.store(
      STORAGE_KEYS.ORGANIZATION,
      JSON.stringify(organization),
    );

    // Emit events
    this.emitEvent('selection-changed');
  }

  /**
   * Select a store
   * @param venue The venue containing the store
   * @param store The store to select
   */
  async selectStore(venue: Venue, store: Store): Promise<void> {
    // Store venue information along with the store
    const storeWithVenue = {
      ...store,
      venue: {
        id: venue.id,
        name: venue.name,
      },
    };

    this.selectionState.store = storeWithVenue;

    // Store the selected store with venue information
    await this.storage.store(
      STORAGE_KEYS.STORE,
      JSON.stringify(storeWithVenue),
    );

    // Emit events
    this.emitEvent('selection-changed');
  }

  /**
   * Select a device
   * @param device The device to select
   */
  async selectDevice(device: T): Promise<void> {
    this.selectionState.device = device;

    // Store the selected device
    await this.storage.store(STORAGE_KEYS.DEVICE, JSON.stringify(device));

    // Emit events
    this.emitEvent('selection-changed');
  }

  /**
   * Load selections from storage
   */
  async loadSelections(): Promise<SelectionState<T>> {
    // Load organization
    const orgData = await this.storage.retrieve(STORAGE_KEYS.ORGANIZATION);
    if (orgData) {
      this.selectionState.organization = JSON.parse(orgData);
    }

    // Load store
    const storeData = await this.storage.retrieve(STORAGE_KEYS.STORE);
    if (storeData) {
      this.selectionState.store = JSON.parse(storeData);
    }

    // Load device
    const deviceData = await this.storage.retrieve(STORAGE_KEYS.DEVICE);
    if (deviceData) {
      this.selectionState.device = JSON.parse(deviceData);
    }

    return this.selectionState;
  }

  /**
   * Clear store and device selections (but keep organization)
   */
  async clearStoreAndDevice(): Promise<void> {
    // Clear store and device from state
    delete this.selectionState.store;
    delete this.selectionState.device;

    // Clear stored selections
    await this.storage.remove(STORAGE_KEYS.STORE);
    await this.storage.remove(STORAGE_KEYS.DEVICE);

    // Emit events
    this.emitEvent('selection-cleared');
    this.emitEvent('selection-changed');
  }

  // Clear device only (organization and store should be preserved)
  async clearDevice(): Promise<void> {
    // Clear device from state
    delete this.selectionState.device;

    // Clear stored selections
    await this.storage.remove(STORAGE_KEYS.DEVICE);

    // Emit events
    this.emitEvent('selection-cleared');
    this.emitEvent('selection-changed');
  }

  /**
   * Clear all selections
   */
  async clearSelections(): Promise<void> {
    this.selectionState = {};

    // Clear stored selections
    await this.storage.remove(STORAGE_KEYS.ORGANIZATION);
    await this.storage.remove(STORAGE_KEYS.STORE);
    await this.storage.remove(STORAGE_KEYS.DEVICE);

    // Emit events
    this.emitEvent('selection-cleared');
    this.emitEvent('selection-changed');
  }
}

// Factory function for creating selection service with storage
export const createSelectionService = <T extends Device = Device>(
  storage: SelectionStorage,
): SelectionService<T> => {
  return SelectionService.getInstance<T>(storage);
};
